import { Component, Input, OnInit, OnDestroy, ViewChild, DestroyRef } from '@angular/core';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, forkJoin } from 'rxjs';
import { filter, finalize, switchMap, take, tap, distinctUntilChanged, map } from 'rxjs/operators';
import * as _ from 'lodash';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import Selection from '@simonwep/selection-js';

import { DeleteDialogService } from '../../../core/services/dialog-services/delete-dialog.service';
import { PlaylistItemDialogService } from '../../../core/services/dialog-services/playlist-item-dialog.service';
import { PlaylistsService } from '../../../core/services/http-services/playlists.service';
import { PlaylistStateService } from '../../../core/services/screen-state/playlists-state.service';
import { UIService } from '../../../core/services/utils/ui.service';
import { ScreensStateService } from '../../../core/services/screen-state/screens-state.service';
import { SchedulesService } from '../../../core/services/http-services/schedules.service';

import { PlaylistDTO } from '../../../core/models/view-models/playlist-models/playlist-dto.model';
import { PlaylistDTOModel } from '../../../core/models/view-models/playlist-models/playlist-dto-crud.model';
import { PlaylistItemDTO } from '../../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { MainLibraryFormComponent } from '../../main-library-form/main-library-form.component';
import { PopupMessagesEnum } from '../../../core/enums/toastr-messages/popup-messages.enum';
import { LibraryEditItemTypeEnum } from './../../../core/enums/library-edit-item-type.enum';
import { LibraryEditItem } from './../../../core/models/library-edit-item.model';
import { AddPlaylistItemsTab } from '../../../core/models/view-models/playlist-models/add-playlist-items-tab.model';
import { PlaylistItemDialogComponent } from '../../modal-playlist-item/playlist-item-dialog.component';
import { ToastrMessageEnum } from '../../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistDialogMessagesEnum } from '../../../core/enums/toastr-messages/playlist-dialog-messages.enum';
import { AppConstants } from '../../../app.constants';
import { DisplayPlaylistDTO } from '../../../core/models/view-models/playlist-models/display-playlist-dto.model';
import { LaunchersService } from '../../../core/services/http-services/launchers.service';
import { ILauncherFeature } from '../../../core/interfaces/launcher-feature.interface';
import { createSelection } from '../../../core/helpers/create-selection.helper';
import { PlaylistItemsService } from '../../../core/services/http-services/playlistItems.service';
import { PlaylistItemType } from '../../../core/models/enums/playlist-item-type.enum';

@Component({
  selector: 'edit-playlist',
  templateUrl: './edit-playlist.component.html',
  styleUrls: ['./edit-playlist.component.scss'],
})
export class EditPlaylistComponent implements OnInit, OnDestroy {
  @Input() playlist: PlaylistDTO;
  @ViewChild(MainLibraryFormComponent, { static: true }) libraryFormComponent: MainLibraryFormComponent;
  public editItem: LibraryEditItem;
  public existedTitles: string[] = [];
  public libraryEditItemTypes = LibraryEditItemTypeEnum;
  public isTitleFocused: boolean = false;
  public isLoading: boolean;
  public selection: Selection;
  public convertingItem$ = this.playlistItemsService.convertingItem;
  public playlistItemType = PlaylistItemType;

  private selectedItems: string[] = [];
  private oldPlaylist: PlaylistDTO; // return old playlist if saving was skipped
  private subscriptions: Subscription = new Subscription();
  private playlistItemsChanged: Subject<AddPlaylistItemsTab> = new Subject();

  constructor(
    public readonly dialog: MatDialog,
    private readonly uiService: UIService,
    private readonly toastr: ToastrService,
    private readonly dataService: PlaylistsService,
    private readonly confirmService: DeleteDialogService,
    private readonly plItemDialog: PlaylistItemDialogService,
    private readonly playlistsStateService: PlaylistStateService,
    private readonly launchersService: LaunchersService,
    private readonly screenStateService: ScreensStateService,
    private readonly schedulesService: SchedulesService,
    private readonly destoryRef: DestroyRef,
    private readonly playlistItemsService: PlaylistItemsService
  ) {
    this.subscriptions.add(
      this.dataService.playlistCollectionChanged.subscribe(
        (data) =>
        (this.existedTitles = data.map((item) =>
          item.PlaylistTitle.toLowerCase()
        ))
      )
    );

    this.subscriptions.add(
      this.playlistItemsChanged.subscribe((data: AddPlaylistItemsTab) => {
        this.editItem = {...this.editItem, PlaylistItems: data.playlistItems};
        if (data.isAddAnother) {
          this.onAddItem(true);
        }
      })
    );
  }

  ngOnInit(): void {
    if (this.playlist.Id) {
      this.existedTitles = this.existedTitles.filter(
        (name) => name !== this.playlist.PlaylistTitle.toLowerCase()
      );
    }
    this.oldPlaylist = _.cloneDeep(this.playlist);
    this.prepareEditItem();
    this.dataService.titleState.subscribe((result: boolean) => {
      this.isTitleFocused = result;
    });

    this.uiService.unselectAllPlaylistItems.pipe(takeUntilDestroyed(this.destoryRef)).subscribe(
      () => this.playlist.PlaylistItems.forEach(item => item.isSelected = false)
    );

    this.playlistsStateService.playlistItemsSelectionChanged.pipe(
      distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
      takeUntilDestroyed(this.destoryRef)
    ).subscribe(data => {
      this.playlist.PlaylistItems.forEach((item, index) => {
        item.isSelected = data.some(idx => index === +idx);
      })
    });

    this.selection = createSelection(
      this,
      ['playlist-item'],
      () => this.playlistsStateService.selectItemByIndex(this.selectedItems),
      AppConstants.PLAYLIST_ITEMS_SELECTION_CLASS_NAME,
      AppConstants.PLAYLIST_ITEMS_SELECTION_AREA_CLASS_NAME,
      AppConstants.PLAYLIST_ITEMS_BOUNDARY_CLASS_NAME
    );
  }

  ngOnDestroy(): void {
    if (this.selection) {
      this.selection.destroy();
    }
    this.subscriptions.unsubscribe();
  }

  private prepareEditItem(): void {
    const neededEditItemFields: Partial<LibraryEditItem> = Object.assign(
      _.pick(this.playlist, Object.keys(new LibraryEditItem())),
      {
        Title: this.playlist.PlaylistTitle,
        Description: this.playlist.PlaylistDescription,
      }
    );

    this.editItem = new LibraryEditItem(
      this.playlist.Id ? neededEditItemFields : {}
    );
  }

  submit(): void {
    if (this.libraryFormComponent.form.invalid) {
      return;
    }

    this.oldPlaylist.PlaylistItems.forEach((playlistItem: PlaylistItemDTO) => {
      if (playlistItem.hasOwnProperty('launcherFeatures')) {
        delete playlistItem.launcherFeatures;
      }
    });

    if (
      this.libraryFormComponent.form.pristine &&
      _.isEqual(this.oldPlaylist.PlaylistItems, this.playlist.PlaylistItems) &&
      !(this.oldPlaylist.IsShared && !this.libraryFormComponent.form.value.IsShared)
    ) {
      this.close();
      this.toastr.success(
        `\'${this.playlist.PlaylistTitle}\' was saved without any changes`,
        'Playlist updating'
      );
      return;
    }

    this.playlistItemsService.validateRss(this.playlist);

    const isLocalPlaylist: boolean = _.some(this.playlist.PlaylistItems, (playlistItem: PlaylistItemDTO) =>
      playlistItem && playlistItem.IsLocal);

    const {
      Title: PlaylistTitle,
      Description: PlaylistDescription,
      IsShared,
      IsSharedWithAllUsers,
      IsRepeated,
      Transition,
      FitToVideoSize
    } = this.libraryFormComponent.form.getRawValue();

    const model = new PlaylistDTOModel({
      PlaylistTitle,
      PlaylistDescription,
      IsRepeated,
      IsAndroid: this.playlist.IsAndroid,
      PlaylistItems: this.playlist.PlaylistItems,
      IsShared: IsShared && (this.libraryFormComponent.UsersWhoHaveAccessIds.length || IsSharedWithAllUsers) ? IsShared : false,
      UsersWhoHaveAccessIds: IsShared ? this.libraryFormComponent.UsersWhoHaveAccessIds: [],
      IsSharedWithAllUsers: IsShared ? IsSharedWithAllUsers : false,
      MachinePhysicalAddress: isLocalPlaylist ? this.playlist.MachinePhysicalAddress : null,
      Transition: this.playlist.IsAndroid ? Transition : null,
      FitToVideoSize: this.playlist.IsAndroid ? FitToVideoSize : false
    });

    if (this.playlist.Id) {
      model.Id = this.playlist.Id;
      this.updatePlaylist(model);
    } else {
      this.createPlaylist(model);
    }

    this.isLoading = true;
  }

  onAddItem(isAddAnotherItem: boolean = false): void {
    this.openItem(this.playlist, [], isAddAnotherItem);
  }

  onEditItem(item: PlaylistItemDTO): void {
    if (!item.isSelected) {
      this.playlist.PlaylistItems.forEach(item => item.isSelected = false);
    }

    this.openItem(
      this.playlist,
      item.isSelected
        ? this.playlist.PlaylistItems.filter(item => item.isSelected)
        : [item]
    );
  }

  onItemDeleted(): void {
    this.editItem = {...this.editItem, PlaylistItems: this.playlist.PlaylistItems};
  }

  drop(event: { previousIndex: number; currentIndex: number }): void {
    if (this.playlist.PlaylistItems[event.currentIndex].PlaylistItemType === PlaylistItemType.Foreground) {
      return;
    }

    moveItemInArray(
      this.playlist.PlaylistItems,
      event.previousIndex,
      event.currentIndex
    );
  }

  exit(): void {
    const playlistItemsWithoutIcons: PlaylistItemDTO[] = _.cloneDeep(this.playlist.PlaylistItems);
    playlistItemsWithoutIcons.forEach(items =>
      items.PlaylistItemLinks.forEach(source =>
        delete source.icon));

    if (
      this.libraryFormComponent.form.dirty ||
      !_.isEqual(this.oldPlaylist.PlaylistItems, playlistItemsWithoutIcons)
    ) {
      this.subscriptions.add(
        this.confirmService
          .show(PopupMessagesEnum.SkipUpdatePlaylist)
          .subscribe((res) => {
            if (res) {
              this.close();
            }
          })
      );
    } else {
      this.close();
    }
  }

  private createPlaylist(model: PlaylistDTOModel): void {
    this.subscriptions.add(
      this.dataService.createPlaylist(model).subscribe((createdPlaylist: DisplayPlaylistDTO) => {
        this.setFeaturesToLauncher(createdPlaylist, model);
        this.close();
        this.toastr.success(
          `\'${model.PlaylistTitle}\' ${ToastrMessageEnum.SuccessfullyCreatedTitle}`,
          PlaylistDialogMessagesEnum.PlaylistCreationTitle
        );
      },
        () => this.close()
      )
    );
  }

  private updatePlaylist(model: PlaylistDTOModel): void {
    this.subscriptions.add(
      this.dataService.updatePlaylist(model).pipe(finalize(() => {
        this.close();
      }),
      tap((updatedPlaylist: DisplayPlaylistDTO) => {
        this.setFeaturesToLauncher(updatedPlaylist, model);
        this.toastr.success(
          `\'${this.playlist.PlaylistTitle}\' ${ToastrMessageEnum.SuccessfullyUpdatedTitle}`,
          PlaylistDialogMessagesEnum.PlaylistUpdatingTitle
        );
      }),
      filter(() => model.IsAndroid !== this.oldPlaylist.IsAndroid),
      switchMap(() => forkJoin([
        this.screenStateService.dismissAsignmentAndStopPlaylist(this.playlist.Id),
        this.schedulesService.removeSchedulesByPlaylistId(this.playlist.Id)
      ])),
      takeUntilDestroyed(this.destoryRef)
    )
    .subscribe());
  }

  private setFeaturesToLauncher(newPlaylist: DisplayPlaylistDTO, playlistModel: PlaylistDTOModel): void {
    playlistModel.PlaylistItems.forEach((playlistItem, index) => {

      if (!playlistItem.Id && playlistItem.launcherFeatures && playlistItem.launcherFeatures.length) {
        const platlistItemId: number = newPlaylist.PlaylistItems[index].Id;

        playlistItem.launcherFeatures.forEach((launcherFeature: ILauncherFeature) => {
          launcherFeature.FeatureValue = `${+launcherFeature.FeatureValue}`;
          launcherFeature.PlaylistItemId = platlistItemId;

          this.launchersService.setFeaturesToLauncher(launcherFeature).pipe(take(1)).subscribe();
        });
      }
    });
  }

  private close(): void {
    this.uiService.hideLibraryEditPanel();
  }

  private openItem(
    playlist: PlaylistDTO,
    playlistItems: PlaylistItemDTO[] = [],
    isAddAnother: boolean = false
  ): void {
    const dialogRef = this.dialog.open(PlaylistItemDialogComponent, AppConstants.FILE_UPLOADING_DIALOG_WINDOW_CONFIG);

    dialogRef.componentInstance.playlist = playlist;
    dialogRef.componentInstance.playlistItems = playlistItems;
    dialogRef.componentInstance.AddAnother = isAddAnother;

    dialogRef.componentInstance.closeRequest.pipe(
      tap(data => this.playlistItemsChanged.next(data)),
      switchMap(data => this.plItemDialog.closeWhenUploading().pipe(map(isToClose => ({isToClose, data})))),
      filter(({isToClose}) => isToClose),
      tap(() => dialogRef.close()),
      takeUntilDestroyed(this.destoryRef)
    ).subscribe();
  }
}
